import type { NodeRelease, NodeVersion } from './data';

export interface BundledNodeRelease {
  readonly version: string;
  readonly bundlePath: string;
  readonly bundleSha256: string;
  readonly commit: string;
}

const VERSION = /^[A-Za-z0-9][A-Za-z0-9._+-]{0,63}$/;
const SHA = /^[0-9a-f]{64}$/;
const COMMIT = /^[0-9a-f]{40}$/;

export function parseBundledNodeRelease(value: unknown): BundledNodeRelease | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const row = value as Record<string, unknown>;
  if (typeof row.version !== 'string' || !VERSION.test(row.version)) return null;
  if (typeof row.bundlePath !== 'string' || !/^\/node\/releases\/[A-Za-z0-9._+-]+\.tgz$/.test(row.bundlePath)) return null;
  if (typeof row.bundleSha256 !== 'string' || !SHA.test(row.bundleSha256)) return null;
  if (typeof row.commit !== 'string' || !COMMIT.test(row.commit)) return null;
  return { version: row.version, bundlePath: row.bundlePath, bundleSha256: row.bundleSha256, commit: row.commit };
}

export function bundleUrl(origin: string, path: string): string {
  return new URL(path, origin).toString();
}

/** M27 rolls one computer at a time. */
export function activeRollout(versions: readonly NodeVersion[]): NodeVersion | null {
  return versions.find((node) => node.behind) ?? null;
}

export type RunningNodeBuildKind = 'unknown_running' | 'unavailable' | 'incomparable' | 'published' | 'merged_not_live';

export interface RunningNodeBuild {
  readonly running: string;
  readonly relation: string;
  readonly kind: RunningNodeBuildKind;
}

interface BuildKey {
  readonly kind: 'git' | 'version';
  readonly value: string;
}

interface DatedRelease {
  readonly version: string;
  readonly key: BuildKey;
  readonly publishedAt: number;
}

const GIT_VERSION = /^git-([0-9a-f]{12,40})$/i;
const FULL_COMMIT = /^[0-9a-f]{40}$/i;
const NOTES_COMMIT = /\bcommit\s+([0-9a-f]{12,40})\b/i;

/** Release versions use the first 12 hex digits. A shorter prefix is not evidence of the same build. */
function sameGit(left: string, right: string): boolean {
  const [shorter, longer] = left.length <= right.length ? [left, right] : [right, left];
  return shorter.length >= 12 && longer.startsWith(shorter);
}

function gitIdentity(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = value.trim().toLowerCase();
  const version = GIT_VERSION.exec(trimmed);
  if (version?.[1]) return version[1];
  const commit = FULL_COMMIT.exec(trimmed);
  return commit?.[0] ?? null;
}

function sameBuild(left: BuildKey, right: BuildKey): boolean {
  if (left.kind === 'git' && right.kind === 'git') return sameGit(left.value, right.value);
  return left.kind === right.kind && left.value === right.value;
}

function runningKey(version: string): BuildKey {
  const git = gitIdentity(version);
  if (git) return { kind: 'git', value: git };
  return { kind: 'version', value: version.trim() };
}

function releaseKey(release: NodeRelease, bundle: BundledNodeRelease | null): BuildKey | null {
  const versionGit = gitIdentity(release.version);
  const notesGit = release.notes ? NOTES_COMMIT.exec(release.notes)?.[1]?.toLowerCase() ?? null : null;
  if (versionGit && notesGit && !sameGit(versionGit, notesGit)) return null;
  let git = versionGit ?? notesGit;
  const matchesBundle = bundle !== null
    && release.version === bundle.version
    && release.bundle_sha256 === bundle.bundleSha256;
  if (matchesBundle && bundle) {
    const bundleGit = gitIdentity(bundle.commit);
    if (bundleGit && git && !sameGit(git, bundleGit)) return null;
    if (bundleGit) git = bundleGit;
  }
  if (git) return { kind: 'git', value: git };
  const version = release.version.trim();
  if (!version) return null;
  return { kind: 'version', value: version };
}

function datedReleases(releases: readonly NodeRelease[], bundle: BundledNodeRelease | null): DatedRelease[] {
  const dated: DatedRelease[] = [];
  for (const release of releases) {
    const key = releaseKey(release, bundle);
    const publishedAt = Date.parse(release.published_at);
    if (key === null || Number.isNaN(publishedAt)) continue;
    dated.push({ version: release.version, key, publishedAt });
  }
  return dated;
}

function newestRelease(rows: readonly DatedRelease[]): DatedRelease | null {
  let newest: DatedRelease | null = null;
  let tied = false;
  for (const row of rows) {
    if (newest === null || row.publishedAt > newest.publishedAt) {
      newest = row;
      tied = false;
    } else if (row.publishedAt === newest.publishedAt && !sameBuild(row.key, newest.key)) {
      tied = true;
    }
  }
  return tied ? null : newest;
}

/**
 * Heartbeat build versus published releases. "Merged not live" is said only when
 * the heartbeat matches an earlier published release and a strictly later one exists.
 * A merge, an unpublished manifest, or builds with no published-release order stay unknown.
 */
export function describeRunningNodeBuild(input: {
  readonly name: string;
  readonly runningVersion: string | null;
  readonly releases: readonly NodeRelease[];
  readonly bundle?: BundledNodeRelease | null;
}): RunningNodeBuild {
  const runningVersion = input.runningVersion?.trim() ?? '';
  const running = runningVersion === '' ? 'running build unknown' : `running build ${runningVersion}`;
  const bundle = input.bundle ?? null;
  const dated = datedReleases(input.releases, bundle);
  if (input.releases.length === 0) {
    return { running, relation: 'published release unavailable', kind: 'unavailable' };
  }
  const newest = newestRelease(dated);
  if (runningVersion === '' || newest === null) {
    return { running, relation: 'cannot tell whether newer merged node changes are live', kind: runningVersion === '' ? 'unknown_running' : 'incomparable' };
  }
  const key = runningKey(runningVersion);
  if (sameBuild(key, newest.key)) {
    return { running, relation: 'running the published build', kind: 'published' };
  }
  const earlier = dated.some((row) => row.publishedAt < newest.publishedAt && sameBuild(row.key, key));
  if (!earlier) {
    return { running, relation: 'cannot tell whether newer merged node changes are live', kind: 'incomparable' };
  }
  const name = input.name.trim() || 'this computer';
  return {
    running,
    relation: `merged not live on ${name} · published ${newest.version}`,
    kind: 'merged_not_live',
  };
}

/** The organization switch. While it is on, the server updates idle computers. No row means on. */
export function automaticIdleUpdatesOn(rows: readonly { readonly automatic_when_idle: boolean }[]): boolean {
  return rows[0]?.automatic_when_idle ?? true;
}

export interface RolloutComputer {
  readonly nodeId: string;
  readonly runningVersion: string | null;
  readonly targetVersion: string | null;
  readonly behind: boolean;
  /** A running task on this computer. An update must not be pointed at it. */
  readonly workingTask: boolean;
}

/** A working task is a running-task row for this computer's name, the Engine Room join. */
export function computersForRollout(
  versions: readonly NodeVersion[],
  running: readonly { readonly node_name: string }[],
): RolloutComputer[] {
  return versions.map((node) => ({
    nodeId: node.node_id,
    runningVersion: node.running_version,
    targetVersion: node.target_version,
    behind: node.behind,
    workingTask: running.some((task) => task.node_name === node.name),
  }));
}

export function verifiedReleaseIsPublished(
  bundle: BundledNodeRelease,
  releases: readonly Pick<NodeRelease, 'version' | 'bundle_sha256'>[],
): boolean {
  return releases.some((release) => release.version === bundle.version && release.bundle_sha256 === bundle.bundleSha256);
}

export interface RolloutPlan {
  /** Publish the verified manifest before any computer is pointed at it. */
  readonly publish: boolean;
  /** The one computer to point at the verified build, or null while a rollout is in progress or none is eligible. */
  readonly targetNodeId: string | null;
}

/**
 * Update all, while Command is open: one computer at a time, and never a computer that has a working task.
 * Publish and target are never both set: the verified release has to exist before a computer is pointed at it.
 * Update all publishes even while every computer that still needs the build is busy, and points the
 * computer once that task is finished. Automatic idle updates run on the server, not here.
 */
export function planComputerRollout(input: {
  readonly bundle: BundledNodeRelease | null;
  readonly releases: readonly Pick<NodeRelease, 'version' | 'bundle_sha256'>[];
  readonly computers: readonly RolloutComputer[];
  readonly mode: 'off' | 'manual';
  readonly suppressedNodeIds?: readonly string[];
}): RolloutPlan {
  const bundle = input.bundle;
  if (input.mode === 'off' || bundle === null) return { publish: false, targetNodeId: null };
  const rolling = input.computers.some((computer) => computer.behind
    || (computer.targetVersion === bundle.version && computer.runningVersion !== bundle.version));
  if (rolling) return { publish: false, targetNodeId: null };
  const suppressed = new Set(input.suppressedNodeIds ?? []);
  const pending = input.computers.filter((computer) => !suppressed.has(computer.nodeId)
    && computer.runningVersion !== bundle.version);
  if (pending.length === 0) return { publish: false, targetNodeId: null };
  const eligible = pending.filter((computer) => !computer.workingTask && computer.targetVersion !== bundle.version);
  if (!verifiedReleaseIsPublished(bundle, input.releases)) return { publish: true, targetNodeId: null };
  return { publish: false, targetNodeId: eligible[0]?.nodeId ?? null };
}

/** True when every computer the owner did not clear is already running the verified build. */
export function computerRolloutSettled(
  bundle: BundledNodeRelease,
  computers: readonly RolloutComputer[],
  suppressedNodeIds: readonly string[],
): boolean {
  const suppressed = new Set(suppressedNodeIds);
  return computers.every((computer) => suppressed.has(computer.nodeId) || computer.runningVersion === bundle.version);
}
